import { create } from 'zustand';
import type { AuthUser, PermissionKey } from '@lms/shared';
import { api, refreshAccessToken, setAccessToken } from '@/lib/api';
import { clearQueryCache } from '@/lib/queryClient';
import { useViewModeStore } from './viewMode';

interface AuthState {
  user: AuthUser | null;
  /** True until the initial silent-refresh attempt has settled. */
  isBootstrapping: boolean;
  /**
   * Whether this session ended because somebody PRESSED sign out, as opposed
   * to lapsing underneath them.
   *
   * The route guard sends an unauthenticated visitor to the login page and
   * records where they were trying to go, so that signing in returns them to
   * it. That is right for a bookmarked link or a session that expired
   * mid-page — and wrong for a deliberate sign-out, where it drops the next
   * person to sign in straight back into the last screen the previous one was
   * reading. Pressing sign out is a decision to leave.
   */
  signedOut: boolean;

  login: (email: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  bootstrap: () => Promise<void>;
  setUser: (user: AuthUser | null) => void;
  /**
   * Swaps just the profile picture on the signed-in user.
   *
   * Its own action rather than `setUser({ ...user, avatarUrl })` at the call
   * site: `setUser` also decides `signedOut`, so spreading a user through it to
   * change one field is borrowing a decision that has nothing to do with the
   * change. The shell reads `avatarUrl` straight from this store, so this is
   * what makes the sidebar update the moment the upload finishes instead of on
   * the next full page load.
   */
  setAvatar: (avatarUrl: string | null) => void;
  /** Clears the flag once the login page has been reached — see `LoginPage`. */
  clearSignedOut: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isBootstrapping: true,
  signedOut: false,

  login: async (email, password) => {
    const { data } = await api.post<{ accessToken: string; user: AuthUser }>('/auth/login', {
      email,
      password,
    });
    setAccessToken(data.accessToken);
    /**
     * Before the new user is set, not after.
     *
     * The cache is keyed on the query, not on who asked, so anything left from
     * the last session would be served to this one — and with a thirty-second
     * `staleTime`, served without even a refetch. Clearing here is the second
     * of two guards; `logout` below is the first, and this one covers the ways
     * a session ends without anybody pressing sign out.
     */
    clearQueryCache();
    /**
     * And into their own working view, whoever they are.
     *
     * The choice is remembered in localStorage, which outlives a session and
     * belongs to the BROWSER rather than to the person. `logout` clears it,
     * but a session can end without anybody pressing sign out — an expired
     * token, a closed tab — and then the next person to sign in inherits a
     * view somebody else chose.
     *
     * For a Trainer that is the difference between landing on the screens they
     * work in and landing in the learner view with no idea the other exists.
     * Same argument as the cache clear above, and the same two-guard shape.
     */
    useViewModeStore.getState().reset();
    set({ user: data.user, signedOut: false });
    return data.user;
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      /**
       * Swallowed, not rethrown.
       *
       * Telling the server is a courtesy — the refresh token it would revoke
       * expires on its own, and the local session is cleared below regardless.
       * Every caller writes `void logout()`, so a rejection here could not be
       * handled by anybody and surfaced as an unhandled promise rejection in
       * the console of a person who had simply signed out on a bad connection.
       */
    } finally {
      // Clear local state even if the network call failed — the user asked to
      // leave, so the session must not appear to survive.
      setAccessToken(null);
      /**
       * And forget what they fetched. Otherwise the next person to sign in on
       * this browser is shown this person's dashboard, courses and team — the
       * API never asked about, because the browser never asked again.
       */
      clearQueryCache();
      /**
       * And back to the administrator's own view.
       *
       * The choice is remembered in localStorage, which outlives the session.
       * Left behind, the next person to sign in on this browser inherits it —
       * and an ordinary employee never sees the switch, so they would be stuck
       * in a view somebody else chose with no way to leave it.
       */
      useViewModeStore.getState().reset();
      set({ user: null, signedOut: true });
    }
  },

  /**
   * Restores a session on page load. The access token is gone (memory only),
   * but the httpOnly refresh cookie survives, so one silent refresh brings the
   * user back without a login prompt.
   */
  bootstrap: async () => {
    const token = await refreshAccessToken();
    if (!token) {
      set({ user: null, isBootstrapping: false });
      return;
    }
    try {
      const { data } = await api.get<{ user: AuthUser }>('/auth/me');
      set({ user: data.user, isBootstrapping: false });
    } catch {
      setAccessToken(null);
      set({ user: null, isBootstrapping: false });
    }
  },

  /**
   * Clearing the user from anywhere else — the change-password screen, which
   * revokes every session — is the same kind of ending as pressing sign out,
   * and must not be resumed into either.
   */
  setUser: (user) => set({ user, signedOut: user === null }),

  // No-op when nobody is signed in, rather than creating half a user.
  setAvatar: (avatarUrl) =>
    set((state) => (state.user ? { user: { ...state.user, avatarUrl } } : {})),

  clearSignedOut: () => set({ signedOut: false }),
}));

/**
 * UI-level permission check.
 *
 * This decides what to RENDER, never what is allowed — the API enforces the
 * same permission independently on every request. Hiding a button is a
 * courtesy; the 403 behind it is the actual control.
 */
export function useHasPermission(...required: PermissionKey[]): boolean {
  const permissions = useAuthStore((s) => s.user?.permissions);
  if (!permissions) return false;
  return required.every((key) => permissions.includes(key));
}

export function useHasAnyPermission(...accepted: PermissionKey[]): boolean {
  const permissions = useAuthStore((s) => s.user?.permissions);
  if (!permissions) return false;
  return accepted.some((key) => permissions.includes(key));
}
